import { ArdoqId, PersistedAttributeQuestion } from '@ardoq/api-types';
import { useEffectOnce } from '@ardoq/hooks';
import { capitalize } from 'lodash';
import { PropsWithChildren } from 'react';
import { t } from '@ardoq/i18n';
import { SurveyErrorMessage } from './surveyQuestionAtoms';
import { SurveyFieldControl } from './FieldQuestion/SurveyFieldControl';
import { getFieldOrAttributeQuestionUserFeedback } from './getUserFeedback';
import { SharedFieldAndAttributeQuestionProps } from './questionsTypes';
import { SurveyTextAreaContainerProps } from './SurveyTextAreaContainer';
import {
  AttributeAnswer,
  AttributeAnswerValue,
  GetAISuggestions,
} from '../types';

const isNameQuestion = (question: PersistedAttributeQuestion) =>
  question.properties.attributeName === 'name';

type SurveyAttributeQuestionProps = SharedFieldAndAttributeQuestionProps & {
  originalValue: AttributeAnswerValue;
  value: AttributeAnswerValue;
  question: PersistedAttributeQuestion;
  answer: AttributeAnswer | undefined;
  defaultParentId?: ArdoqId | null;
  parentName: string | undefined;
  getAISuggestions: GetAISuggestions;
} & Required<
    Pick<
      SurveyTextAreaContainerProps,
      'onDescriptionReceived' | 'onLLMCouldNotGenerateDescription'
    >
  >;

export const SurveyAttributeQuestion = ({
  surveyId,
  question,
  answer,
  readonly,
  value,
  originalValue,
  type,
  validateDuplicateName,
  isLocked,
  currentLocale,
  defaultParentId,
  componentName,
  componentTypeName,
  componentId,
  children,
  isReference,
  hasAttemptedSubmission,
  hasAutocompleteFeature,
  isEmbedded,
  parentName,
  hideReadOnlyLabel,
  QuestionContentWrapper,
  highlightUnansweredPriorityQuestions,
  i18nLocale,
  getExistingComponentByName,
  setAnswer,
  getDuplicate,
  generateComponentDescription,
  onDescriptionReceived,
  onLLMCouldNotGenerateDescription,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  userOptions,
}: PropsWithChildren<SurveyAttributeQuestionProps>) => {
  const questionId = question._id;
  const validators = question.validators;
  const attributeName = question.properties.attributeName;

  useEffectOnce(() => {
    if (value === undefined || originalValue !== undefined) return;
    setAnswer({
      questionId,
      value: attributeName === 'parent' ? defaultParentId : undefined,
    });
  });

  const isMandatory = Boolean(
    !isLocked &&
    validators &&
    validators.some(validator => validator.type === 'mandatory')
  );

  const fieldLabel = capitalize(attributeName);

  const userFeedback = getFieldOrAttributeQuestionUserFeedback({
    isMandatory,
    value,
    type,
    fieldLabel,
    i18nLocale,
  });

  // we can't set the name question to readonly when creating a new component
  const isNamingNewComponent = isNameQuestion(question) && !componentId;
  const readOnlyOrLocked = (readonly || isLocked) && !isNamingNewComponent;

  const noFieldTypeFoundMessage =
    type === undefined
      ? t(
          i18nLocale,
          'surveysUi:validation.fieldNotFound',
          'Field type not found. Was the field removed from the workspace?'
        )
      : null;

  return (
    <QuestionContentWrapper
      question={question}
      answer={answer}
      highlightUnansweredPriorityQuestions={
        highlightUnansweredPriorityQuestions
      }
      i18nLocale={i18nLocale}
    >
      {type ? (
        <>
          <SurveyFieldControl
            userOptions={userOptions}
            userFeedback={userFeedback}
            type={type}
            readOnlyOrLocked={readOnlyOrLocked}
            surveyId={surveyId}
            questionId={questionId}
            componentId={componentId}
            setAnswer={setAnswer}
            currentLocale={currentLocale}
            i18nLocale={i18nLocale}
            originalValue={originalValue}
            attributeName={attributeName}
            value={value}
            validateDuplicateName={validateDuplicateName}
            getDuplicate={getDuplicate}
            componentName={componentName}
            componentTypeName={componentTypeName}
            isReference={isReference}
            isEmbedded={isEmbedded}
            hasAttemptedSubmission={hasAttemptedSubmission}
            hasAutocompleteFeature={hasAutocompleteFeature}
            hideReadOnlyLabel={hideReadOnlyLabel}
            questionLabel={fieldLabel}
            getExistingComponentByName={getExistingComponentByName}
            generateComponentDescription={generateComponentDescription}
            onDescriptionReceived={onDescriptionReceived}
            onLLMCouldNotGenerateDescription={onLLMCouldNotGenerateDescription}
            field={undefined}
            parentName={parentName}
            getAISuggestions={getAISuggestions}
            fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
          />
        </>
      ) : (
        noFieldTypeFoundMessage && (
          <SurveyErrorMessage>{noFieldTypeFoundMessage}</SurveyErrorMessage>
        )
      )}
      {children}
    </QuestionContentWrapper>
  );
};
